import { type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { cn } from '@/lib/cn';

export interface ModalProps {
  isOpen: boolean;
  title: string;
  /**
   * Optional accessible description. When provided, rendered as a
   * screen-reader-only `Dialog.Description` so Radix can wire its id to
   * `aria-describedby`. When omitted, `aria-describedby={undefined}` is
   * passed to `Dialog.Content` to silence the Radix warning that fires
   * when neither a Description child nor an explicit undefined opt-out is
   * present (Radix Dialog v1.1+).
   */
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}

export function Modal({
  isOpen,
  title,
  description,
  onClose,
  children,
  footer,
  className,
}: ModalProps) {
  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2',
            'flex max-h-[90vh] flex-col rounded-xl bg-white shadow-2xl',
            'focus:outline-none',
            className,
          )}
          // When `description` is provided, leave aria-describedby unset so
          // Radix can auto-wire it to the rendered `Dialog.Description`'s id
          // (its default behaviour). When no description is provided, set
          // `aria-describedby={undefined}` to override Radix's auto-wire and
          // silence the "Missing Description" warning intentionally.
          {...(description ? {} : { 'aria-describedby': undefined })}
        >
          <div className="flex shrink-0 items-center justify-between border-b border-[#e8e8e8] px-6 py-4">
            <Dialog.Title className="text-lg font-semibold text-[#333]">{title}</Dialog.Title>
            {description && (
              <Dialog.Description className="sr-only">{description}</Dialog.Description>
            )}
            <Dialog.Close
              className="rounded-md p-1 text-[#999] transition-colors hover:bg-[#f0f0f0] hover:text-[#333] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6B0F1A]"
              aria-label="Close"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </Dialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>

          {footer && (
            <div className="flex shrink-0 items-center justify-end gap-3 border-t border-[#e8e8e8] px-6 py-4">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
